import { useState, useEffect } from 'react'
import AdminTable from '../../components/admin/AdminTable'
import AdminForm from '../../components/admin/AdminForm'
import { motion, AnimatePresence } from 'framer-motion'

const API = import.meta.env.VITE_API_URL || '/api'

function getHeaders() {
  const token = localStorage.getItem('token')
  return { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }
}

const fields = [
  { name: 'title', label: 'Title', type: 'text' as const, required: true, placeholder: 'Web Design' },
  { name: 'icon', label: 'Icon (emoji)', type: 'text' as const, placeholder: '🎨' },
  { name: 'description', label: 'Description', type: 'textarea' as const, placeholder: 'Service description...' },
  { name: 'gradient', label: 'Gradient (Tailwind)', type: 'text' as const, placeholder: 'from-electric-blue via-purple-400 to-electric-purple' },
  { name: 'features', label: 'Features (JSON array)', type: 'json' as const, placeholder: '["Feature 1", "Feature 2"]' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' as const, placeholder: '1' },
]

export default function AdminServices() {
  const [items, setItems] = useState<any[]>([])
  const [editing, setEditing] = useState<any | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchItems = async () => {
    const res = await fetch(`${API}/services`)
    setItems(await res.json())
    setLoading(false)
  }

  useEffect(() => { fetchItems() }, [])

  const handleSave = async (data: any) => {
    setSaving(true)
    const isNew = !editing
    const res = await fetch(isNew ? `${API}/services` : `${API}/services/${data.id}`, {
      method: isNew ? 'POST' : 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    })
    if (res.ok) {
      await fetchItems()
      setShowForm(false)
      setEditing(null)
    }
    setSaving(false)
  }

  const handleDelete = async (item: any) => {
    const res = await fetch(`${API}/services/${item.id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    })
    if (res.ok) await fetchItems()
  }

  const columns = [
    { key: 'icon', label: '', render: (v: string) => <span className="text-xl">{v}</span> },
    { key: 'title', label: 'Title', render: (v: string) => <span className="font-medium">{v}</span> },
    { key: 'sort_order', label: 'Order' },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Services</h1>
        <button onClick={() => { setEditing(null); setShowForm(true) }} className="px-4 py-2 bg-electric-blue hover:bg-electric-blue/80 text-white rounded-lg transition-colors text-sm">
          + Add Service
        </button>
      </div>

      <div className="bg-gray-900 border border-white/10 rounded-xl p-5">
        <AdminTable columns={columns} data={items} onEdit={(item) => { setEditing(item); setShowForm(true) }} onDelete={handleDelete} loading={loading} />
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-20 p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl">
              <h2 className="text-xl font-bold text-white mb-6">{editing ? 'Edit Service' : 'Add Service'}</h2>
              <AdminForm
                fields={fields}
                initial={editing || { id: `svc-${Date.now()}`, icon: '📦', title: '', description: '', features: [], gradient: '', sort_order: 0 }}
                onSubmit={handleSave}
                onCancel={() => { setShowForm(false); setEditing(null) }}
                loading={saving}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
